interface BrandingPreviewProps {
    /** Se mantienen por compatibilidad con el caller; el preview YA NO usa colores
     *  (branding = solo logo/favicon; los colores los fija el diseño editorial). */
    primaryHex?: string;
    secondaryHex?: string;
    accentHex?: string;
    logoUrl: string;
    /** Ancho del logo en el sidebar, en % (15–100). */
    logoWidth: number;
    /** Favicon opcional: si viene, se muestra en la "pestaña" del header del preview. */
    faviconUrl?: string;
    /** Etiqueta del sidebar cuando no hay logo. */
    sidebarTitle?: string;
}

/**
 * Preview en vivo del branding, con el MISMO diseño (editorial) de la app real: sidebar claro,
 * tinta, hairlines. Sirve para ver cómo queda el LOGO (y su tamaño) + el favicon en la app.
 * Los colores ya no se editan, así que el preview no los usa; se rinde con los tokens del diseño.
 */
export function BrandingPreview({
    logoUrl,
    logoWidth,
    faviconUrl,
    sidebarTitle = 'Mi Tenant',
}: BrandingPreviewProps) {
    return (
        <div
            data-testid="preview"
            className="overflow-hidden rounded-xl border border-border bg-background text-foreground"
        >
            <div className="flex">
                {/* Mini sidebar editorial (espejo del AppShell real). */}
                <div className="w-40 shrink-0 space-y-1 border-r border-border p-3 text-xs">
                    {logoUrl ? (
                        <img
                            src={logoUrl}
                            alt="Logo"
                            className="mx-auto mb-3 block h-auto"
                            style={{ width: `${String(logoWidth)}%` }}
                        />
                    ) : (
                        <div className="mb-3 font-semibold">{sidebarTitle}</div>
                    )}
                    <div className="rounded-lg bg-card px-2 py-1 font-semibold shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
                        Inicio
                    </div>
                    <div className="rounded-lg px-2 py-1 text-foreground/70">Usuarios</div>
                    <div className="rounded-lg px-2 py-1 text-foreground/70">Roles</div>
                </div>

                {/* Mini contenido */}
                <div className="flex-1">
                    <div className="flex items-center justify-between gap-2 border-b border-border bg-background/70 px-3 py-2 text-xs font-semibold backdrop-blur">
                        <span>Mi perfil</span>
                        {/* "Pestaña del navegador" con el favicon. */}
                        <span
                            className="flex items-center gap-1 rounded-md border border-border bg-card px-2 py-0.5 text-[10px] font-medium"
                            title="Vista previa de la pestaña del navegador"
                        >
                            {faviconUrl ? (
                                <img
                                    src={faviconUrl}
                                    alt="Vista previa del favicon"
                                    className="block size-4 object-contain"
                                    width={16}
                                    height={16}
                                />
                            ) : (
                                <span className="block size-4 rounded-sm bg-foreground/20" />
                            )}
                            <span className="opacity-70">Pestaña</span>
                        </span>
                    </div>
                    <div className="space-y-2 p-3">
                        <p className="text-xs font-semibold">Página de ejemplo</p>
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="rounded-lg bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
                                Acción
                            </span>
                            <span className="rounded-lg border border-border bg-card px-3 py-1 text-xs font-medium">
                                Secundario
                            </span>
                        </div>
                        <div className="mt-1 h-2 w-2/3 rounded bg-muted" />
                        <div className="h-2 w-1/2 rounded bg-muted" />
                    </div>
                </div>
            </div>
        </div>
    );
}
